/**
 * @module builder
 */
import type { QuoteDocument, TieredPackageLevel, TieredPackageTier, TieredPackageTierId } from 'paintscout'
// Deep imports (not the `../../../calculator` barrel): this module is pulled in eagerly by
// `builder/quote/index.ts`, and the calculator barrel re-exports `calculate.ts`, which imports back
// into `builder` — going through the barrel here closes that cycle and can leave sibling builder
// exports (e.g. getCrews) undefined depending on module init order.
import { parseNumber } from '../../../calculator/util/parse-number'
import { resolveTierPrice } from '../../../calculator/util/resolve-tier-price'
import { roundPrice } from '../../../calculator/util/round-price'
import { TIER_ORDER } from './default-tiered-packages'
import { getIncludedFeatureLabels } from './tiered-package-features'

export interface TierPriceInfo {
  tierId: TieredPackageTierId
  level: TieredPackageLevel
  name: string
  tagline?: string
  features?: string[]
  contentType?: 'features' | 'description' | 'both'
  description?: string
  isBase: boolean
  isRecommended: boolean
  isSelected: boolean
  /** Signed delta vs the base estimate. */
  delta: number
  /** Resolved total price for the tier. */
  price: number
  /** Signed percent vs the base estimate. */
  percent: number
}

/**
 * Resolve the display price + delta for each tier, relative to the base estimate
 * (the quote subtotal). Pure — does not mutate or recalculate the quote.
 */
export function getTierPrices(args: { quote: QuoteDocument }): Record<TieredPackageTierId, TierPriceInfo> | null {
  const { quote } = args
  const tieredPackages = quote.tieredPackages
  if (!tieredPackages || !tieredPackages.tiers) {
    return null
  }

  const basePrice = parseNumber(quote.totals?.price)

  const resolve = (tierId: TieredPackageTierId): TierPriceInfo => {
    const tier: TieredPackageTier = tieredPackages.tiers[tierId]
    const isBase = tierId === tieredPackages.baseTierId

    // Base tier always resolves to the quote subtotal. A tier that swaps products carries a
    // resolvedPrice stamped from a real recalculation (see setTieredPackages) — its percentage
    // adjustment does not apply, and the customer view has no way to compute the swap itself.
    // Everything else uses the shared resolver so card prices match what the calculator folds
    // into the total.
    const resolvedPrice = !isBase && typeof tier?.resolvedPrice === 'number' ? tier.resolvedPrice : undefined
    const price = roundPrice(isBase ? basePrice : resolvedPrice ?? resolveTierPrice(basePrice, tier?.adjustment))

    const delta = roundPrice(price - basePrice)
    const percent = basePrice ? roundPrice((delta / basePrice) * 100) : 0

    return {
      tierId,
      level: tier?.level,
      name: tier?.name,
      tagline: tier?.tagline,
      // Resolved from the shared feature rows (legacy per-tier arrays migrate on read).
      features: getIncludedFeatureLabels(tieredPackages, tierId),
      contentType: tier?.contentType,
      description: tier?.description,
      isBase,
      isRecommended: tierId === tieredPackages.recommendedTierId,
      isSelected: tierId === tieredPackages.selectedTierId,
      delta,
      price,
      percent
    }
  }

  return TIER_ORDER.reduce(
    (acc, tierId) => {
      acc[tierId] = resolve(tierId)
      return acc
    },
    {} as Record<TieredPackageTierId, TierPriceInfo>
  )
}
